import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Modal,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconSearch from '../../../assets/svg/search-icon-grey.svg';
import axiosInstant from '../../../api';
import { DotLottleLoader, LoadMoreBtn } from '../../../components/Loader';
import { IProduct } from '../../../components/Forms/Product/constants';
import { SingleProduct } from './SingleProduct';
import { IconProduct } from '../../../utils/icons';

interface Props {
  onClose: () => void;
  onSave: (selectedExperts: number[]) => void;
  products: IProduct[];
}

const LIMIT = 20;

export default function UpdateProductListModal({
  onClose,
  products,
  onSave,
}: Props) {
  const timeoutId = useRef<NodeJS.Timeout>();
  const prevProductIds = useRef(products.map((product) => product.desireid));

  const [productsList, setProductsList] = useState<IProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState(
    products.map((product) => product.desireid)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');

  const fetchExperts = async () => {
    setIsLoading(true);

    try {
      const response = await axiosInstant.get('/desires', {
        params: { limit: LIMIT, page, search },
      });
      const responseData = response.data.rows || [];

      setHasMore(responseData.length && responseData.length >= LIMIT);

      const filteredList = (
        page === 1 ? responseData : [...productsList, ...responseData]
      ).filter(
        (product: IProduct) =>
          product.desireid && !prevProductIds.current.includes(product.desireid)
      );
      setProductsList(filteredList);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleSelectProduct = async (productId: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedProducts((prev) => [...prev, productId]);
    } else {
      setSelectedProducts((prev) => prev.filter((id) => id !== productId));
    }
  };

  const handleSave = () => {
    const validIds = selectedProducts.filter((id) => id !== undefined);
    onSave((validIds as any) || []);
    onClose();
  };

  useEffect(() => {
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      fetchExperts();
    }, 400);
    return () => {
      clearTimeout(timeoutId.current);
      timeoutId.current = undefined;
    };
  }, [search, page]);

  const hasNewSelections = selectedProducts.some(
    (id) => !prevProductIds.current.includes(id)
  );

  return (
    <Modal open onClose={onClose}>
      <Box
        component={Paper}
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-[90vh] bg-surface rounded-xl box-shadow-24 flex flex-col px-6 w-[931px] overflow-y-auto"
      >
        <Box className="flex justify-between items-center sticky top-0 bg-surface z-10 py-4 w-full">
          <div className="flex items-center w-100 gap-20">
            <Typography className="text-text-1 !text-2xl !font-bold whitespace-nowrap">
              Products
            </Typography>

            <div className="w-full relative">
              <img
                className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
                alt="search-icon"
                src={IconSearch}
              />
              <input
                type="text"
                className="w-[333px] h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
                id="search-products"
                placeholder="Search"
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <hr className="border-border-token" />
        <div className="h-[600px] max-h-[80vh] overflow-y-auto w-full">
          {((isLoading && page !== 1) || !isLoading) && productsList.length > 0 && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {productsList.map((product) => (
                  <SingleProduct
                    key={product.desireid}
                    product={product}
                    isSelected={selectedProducts.includes(product.desireid)}
                    onSelect={(isChecked) =>
                      handleSelectProduct(Number(product.desireid), isChecked)
                    }
                  />
                ))}
              </div>
            )}
          {isLoading && page === 1 && (
            <Box className="flex justify-center mt-3">
              <DotLottleLoader />
            </Box>
          )}
          {Boolean(hasMore && productsList.length) && (
            <LoadMoreBtn onClick={() => setPage((prevPage) => prevPage + 1)} loading={isLoading} customClasses="!my-3" />
          )}
          {!isLoading && !productsList.length && (
            <div className="flex flex-col items-center justify-center gap-7 h-full w-full">
              <IconProduct width={36} height={36} stroke="#B0B0B0" strokeWidth={1.5} />
              <div className="text-text-2 font-circular text-[14px] font-[450] leading-normal not-italic text-center">
                No products found
              </div>
            </div>
          )}
        </div>
        {!isLoading && productsList.length > 0 && (
          <Box className="flex items-center justify-end py-5 sticky bottom-0 bg-surface">
            <Button
              variant="contained"
              onClick={handleSave}
              size="large"
              disabled={!hasNewSelections}
            >
              Update
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
